import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Input, Select, InputNumber, message, Button, Row, Col, Tooltip, DatePicker, TimePicker } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { createVacancyRequest, fetchVacancyRequests } from '../../redux/vacancyRequestsSlice';
import dayjs from 'dayjs';
import ConfirmModal from '../ConfirmModal';
const { TextArea } = Input;
const { Option } = Select;

const workSchedules = [
    '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
    'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
    'Новая вакансия',
    'Внутренний перевод',
    'Декрет',
    'Замена'
];

const CopyModal = ({
    visible,
    onCancel,
    originalRequest,
    organisations,
    users
}) => {
    const dispatch = useDispatch();
    const { orgId, user } = useSelector((state) => state.auth);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [selectedOrganisation, setSelectedOrganisation] = useState(null);
    const { showAllRequests } = useSelector(state => state.vacancyRequests);
    const [filteredDepartments, setFilteredDepartments] = useState([]);

    const initialValuesRef = useRef({});

    const filterDepartments = useCallback((companyId) => {
        const numericCompanyId = Number(companyId);
        const org = organisations.find(o => o.organisation_id === numericCompanyId);
        setFilteredDepartments(org?.departments || []);
    }, [organisations]);

    const [requiresCar, setRequiresCar] = useState(false);
    const [requiresLicense, setRequiresLicense] = useState(false);
    const [compensateCar, setCompensateCar] = useState(false);
    const [compensatePhone, setCompensatePhone] = useState(false);
    const [requiresRelocation, setRequiresRelocation] = useState(false);
    const [requiresTravel, setRequiresTravel] = useState(false);
    const isMaterialResponsible = Form.useWatch('is_material_responsible', form);

    const handleCheckboxToggle = (fieldName) => {
        const currentValue = form.getFieldValue(fieldName) || false;
        form.setFieldsValue({ [fieldName]: !currentValue });
    };

    const getValidationRules = useCallback((fieldName, isDraft = false) => {
        const draftRequiredFields = {
            company: { required: true, message: 'Выберите компанию' },
            job_title: { required: true, message: 'Укажите название должности' }
        };

        if (isDraft) {
            return draftRequiredFields[fieldName] ? [draftRequiredFields[fieldName]] : [];
        }

        const requiredFields = {
            company: { required: true, message: 'Выберите компанию' },
            submission_date: { required: true, message: 'Укажите дату подачи заявки' },
            department: { required: true, message: 'Выберите отдел' },
            city: { required: true, message: 'Укажите город' },
            planned_start_date: { required: true, message: 'Укажите планируемую дату выхода сотрудника на работу' },
            author_name: { required: true, message: 'Укажите ФИО автора заявки' },
            author_position: { required: true, message: 'Укажите должность автора заявки' },
            job_title: { required: true, message: 'Укажите название должности' },
            positions_count: { required: true, message: 'Укажите количество позиций' },
            rate: { required: true, message: 'Выберите ставку' },
            vacancy_reason: { required: true, message: 'Выберите причину возникновения вакансии' },
            supervisor: { required: true, message: 'Укажите начальника' },
            subordinates_count: { required: true, message: 'Укажите количество подчиненных' },
            is_material_responsible: { required: true, message: 'Укажите материально ответственное лицо' },
            tasks: { required: true, message: 'Опишите задачи' },
            expected_results: { required: true, message: 'Опишите ожидаемые результаты' },
            functional_responsibilities: { required: true, message: 'Опишите функциональные обязанности' },
            work_schedule: { required: true, message: 'Выберите график работы' },
            work_start_time: { required: true, message: 'Укажите время начала работы' },
            work_end_time: { required: true, message: 'Укажите время окончания работы' },
            salary_structure: { required: true, message: 'Опишите структуру заработной платы' },
            probation_salary_min: { required: true, message: 'Укажите минимальную зарплату на период испытательного срока' },
            probation_salary_max: { required: true, message: 'Укажите максимальную зарплату на период испытательного срока' },
            regular_salary_min: { required: true, message: 'Укажите минимальную зарплату после испытательного срока' },
            regular_salary_max: { required: true, message: 'Укажите максимальную зарплату после испытательного срока' },
            work_address: { required: true, message: 'Укажите адрес рабочего места' }
        };

        return requiredFields[fieldName] ? [requiredFields[fieldName]] : [];
    }, []);

    const updateFormValidation = useCallback((isDraft) => {
        if (isDraft) {
            // Для черновика только минимальные требования
            const draftFields = ['company', 'job_title'];
            draftFields.forEach(fieldName => {
                const rules = getValidationRules(fieldName, isDraft);
                form.setFields([{ name: fieldName, rules }]);
            });
        } else {
            // Для создания заявки - все обязательные поля
            const fields = [
                'company', 'submission_date', 'department', 'city',
                'planned_start_date', 'author_name', 'author_position', 'job_title',
                'positions_count', 'rate', 'vacancy_reason', 'supervisor', 'subordinates_count',
                'is_material_responsible', 'tasks', 'expected_results', 'functional_responsibilities',
                'work_schedule', 'work_start_time', 'work_end_time',
                'salary_structure', 'probation_salary_min',
                'probation_salary_max', 'regular_salary_min', 'regular_salary_max', 'work_address'
            ];

            fields.forEach(fieldName => {
                const rules = getValidationRules(fieldName, isDraft);
                form.setFields([{ name: fieldName, rules }]);
            });
        }
    }, [form, getValidationRules]);

    useEffect(() => {
        if (visible && originalRequest) {
            form.setFieldsValue({
                company: originalRequest.organisation,
                approver_management: originalRequest.approver_management || [],
                approver_hr: originalRequest.approver_hr,
                job_title: originalRequest.job_title,
                positions_count: originalRequest.positions_count,
                department: originalRequest.department,
                assignee_id: originalRequest.assignee_id,
                planned_start_date: originalRequest.planned_start_date,
                rate: originalRequest.rate,
                city: originalRequest.city,
                is_material_responsible: originalRequest.is_material_responsible,
                description: originalRequest.description,
                requirements: originalRequest.requirements,
                functional_responsibilities: originalRequest.functional_responsibilities,
                conditions: originalRequest.conditions,
                supervisor: originalRequest.supervisors,
                vacancy_reason: originalRequest.vacancy_reason,
                subordinates_count: originalRequest.subordinates_count,
                tasks: originalRequest.tasks,
                expected_results: originalRequest.expected_results,
                reporting: originalRequest.reporting_details,
                work_schedule: originalRequest.work_schedule,
                work_start_time: originalRequest.work_start_time ? dayjs(originalRequest.work_start_time, 'HH:mm') : undefined,
                work_end_time: originalRequest.work_end_time ? dayjs(originalRequest.work_end_time, 'HH:mm') : undefined,
                work_location_ratio: originalRequest.field_vs_office,
                business_trips_details: originalRequest.travel_details,
                salary_structure: originalRequest.salary_structure,
                probation_salary_min: originalRequest.trial_salary?.split(' - ')[0],
                probation_salary_max: originalRequest.trial_salary?.split(' - ')[1],
                regular_salary_min: originalRequest.post_trial_salary?.split(' - ')[0],
                regular_salary_max: originalRequest.post_trial_salary?.split(' - ')[1],
                work_address: originalRequest.workplace_address,
                gender: originalRequest.gender,
                age: originalRequest.age,
                marital_status: originalRequest.marital_status,
                education: originalRequest.education_levels,
                driver_license_categories: originalRequest.license_categories,
                computer_skills: originalRequest.computer_skills_level,
                skills_1c: originalRequest.skills_1c,
                work_experience: originalRequest.experience_description,
                personal_qualities: originalRequest.personal_traits,
                professional_skills: originalRequest.professional_skills,
                preferred_companies: originalRequest.preferred_companies,
                undesired_companies: originalRequest.unwanted_companies,
                other_conditions: originalRequest.other_conditions,
                author_position: originalRequest.author_position
            });

            setTimeout(() => {
                let firstName = '';
                let lastName = '';

                if (user && user.firstName && user.lastName) {
                    firstName = user.firstName;
                    lastName = user.lastName;
                } else {
                    firstName = localStorage.getItem('first_name') || '';
                    lastName = localStorage.getItem('last_name') || '';
                }

                const authorName = `${firstName} ${lastName}`.trim();

                const defaultValues = {
                    submission_date: dayjs(),
                };

                if (authorName) {
                    defaultValues.author_name = authorName;
                }

                form.setFieldsValue(defaultValues);

                // Сохраняем начальные значения для отслеживания изменений
                setTimeout(() => {
                    initialValuesRef.current = form.getFieldsValue(true);
                    setHasUnsavedChanges(false);
                }, 100);
            }, 0);

            setRequiresCar(originalRequest.requires_car || false);
            setRequiresLicense(originalRequest.requires_license || false);
            const compensateCarValue = originalRequest.compensate_car || false;
            const compensatePhoneValue = originalRequest.compensate_phone || false;
            setCompensateCar(compensateCarValue);
            setCompensatePhone(compensatePhoneValue);
            setRequiresRelocation(originalRequest.requires_relocation || false);
            setRequiresTravel(originalRequest.requires_travel || false);
            setSelectedOrganisation(originalRequest.organisation);
            filterDepartments(originalRequest.organisation);

            // Устанавливаем значения компенсации в форму
            form.setFieldsValue({
                communication_compensation: compensatePhoneValue,
                car_compensation: compensateCarValue
            });

            // Инициализируем валидацию формы
            updateFormValidation(false);
        }
    }, [visible, originalRequest, form, filterDepartments, updateFormValidation, user]);

    const checkForChanges = useCallback(() => {
        const currentValues = form.getFieldsValue(true);
        return JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
    }, [form]);

    const handleFormChange = useCallback(() => {
        setHasUnsavedChanges(checkForChanges());
    }, [checkForChanges]);

    useEffect(() => {
        if (visible && originalRequest) {
            setTimeout(() => {
                initialValuesRef.current = form.getFieldsValue(true);
                setHasUnsavedChanges(false);
            }, 0);
        }
    }, [visible, originalRequest, form]);

    const handleCancel = useCallback(() => {
        form.resetFields();
        setSelectedOrganisation(null);
        setRequiresCar(false);
        setRequiresLicense(false);
        setCompensateCar(false);
        setCompensatePhone(false);
        setRequiresRelocation(false);
        setRequiresTravel(false);
        // setIsMaterialResponsible(false);
        // setDriverLicenseRequired(false);
        onCancel();
    }, [form, onCancel]);

    const handleModalCancel = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowConfirm(true);
        } else {
            handleCancel();
        }
    }, [hasUnsavedChanges, handleCancel]);

    const handleConfirmClose = useCallback((shouldClose) => {
        if (shouldClose) {
            handleCancel();
        }
        setShowConfirm(false);
    }, [handleCancel]);

    const handleSubmit = async (values, isDraft = false) => {
        if (!originalRequest) return;

        try {
            const approversManagement = values.approver_management || [];
            const approverHr = values.approver_hr;
            const movements = [
                //     ...approversManagement.map(approverId => (approverId)),
                //     approverHr
                // ];
                ...approversManagement.filter(id => id != null),
                ...(approverHr ? [approverHr] : [])
            ].filter(id => id != null);

            const submissionDate = values.submission_date ? dayjs(values.submission_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

            const newRequestData = {
                submission_date: submissionDate,
                organisation: values.company,
                department: values.department,
                city: values.city,
                planned_start_date: values.planned_start_date,
                author: Number(user.id),
                author_position: values.author_position,
                is_template: isDraft,
                status: 'pending',
                job_title: values.job_title,
                positions_count: values.positions_count || 1,
                rate: values.rate,
                vacancy_reason: values.vacancy_reason,
                supervisors: values.supervisor,
                subordinates_count: values.subordinates_count ? String(values.subordinates_count) : '0',
                is_material_responsible: isMaterialResponsible || false,
                tasks: values.tasks,
                expected_results: values.expected_results,
                functional_responsibilities: values.functional_responsibilities,
                reporting_details: values.reporting,
                work_schedule: values.work_schedule,
                work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : '',
                work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : '',
                field_vs_office: values.work_location_ratio,
                requires_travel: requiresTravel || false,
                travel_details: values.business_trips_details,
                requires_relocation: requiresRelocation || false,
                salary_structure: values.salary_structure,
                trial_salary: values.probation_salary_min !== undefined && values.probation_salary_max !== undefined ? `${values.probation_salary_min} - ${values.probation_salary_max}` : '',
                post_trial_salary: values.regular_salary_min !== undefined && values.regular_salary_max !== undefined ? `${values.regular_salary_min} - ${values.regular_salary_max}` : '',
                compensate_phone: compensatePhone || false,
                compensate_car: compensateCar || false,
                workplace_address: values.work_address,
                gender: values.gender,
                age: values.age,
                marital_status: values.marital_status || [],
                requires_license: requiresLicense || false,
                license_categories: values.driver_license_categories || [],
                requires_car: requiresCar || false,
                education_levels: values.education || [],
                computer_skills_level: values.computer_skills,
                skills_1c: values.skills_1c,
                experience_description: values.work_experience,
                personal_traits: values.personal_qualities,
                professional_skills: values.professional_skills,
                preferred_companies: values.preferred_companies,
                unwanted_companies: values.undesired_companies,
                other_conditions: values.other_conditions,
                movements: movements
            };

            await dispatch(createVacancyRequest({
                organisationId: orgId,
                vacancyRequest: newRequestData
            })).unwrap();

            if (showAllRequests) {
                dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
            } else {
                dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: orgId })).unwrap();
            }

            message.success(isDraft ? 'Черновик успешно сохранен' : 'Заявка успешно сохранена');
            handleCancel();
        } catch (error) {
            console.error('Ошибка при копировании заявки:', error);
            if (error?.errorFields?.length > 0) {
                form.scrollToField(error.errorFields[0].name, {
                    behavior: 'smooth',
                    block: 'center',
                });
            } else {
                message.error('Ошибка при копировании заявки');
            }
        }
    };

    const handleFormSubmit = async (isDraft = false) => {
        try {
            setLoading(true);
            updateFormValidation(isDraft);

            let values;

            if (isDraft) {
                // Для черновика проверяем только минимальные поля
                const draftRequiredFields = ['company', 'job_title'];
                await form.validateFields(draftRequiredFields);
                values = form.getFieldsValue();
            } else {
                // Для создания заявки проверяем все обязательные поля
                values = await form.validateFields();
            }

            await handleSubmit(values, isDraft);
        } catch (error) {
            console.error('Ошибка валидации формы:', error);
            if (error?.errorFields?.length > 0) {
                form.scrollToField(error.errorFields[0].name, {
                    behavior: 'smooth',
                    block: 'center',
                });
                message.error('Пожалуйста, заполните все обязательные поля');
            }
        } finally {
            setLoading(false);
        }
    };

    const getOrganisationDepartments = (organisationId) => {
        const organisation = organisations?.find(org => org.organisation_id === organisationId);
        return organisation?.departments || [];
    };

    const getOrganisationName = (id) => {
        const org = organisations?.find(o => o.organisation_id === id);
        return org ? org.name : 'Не указано';
    };

    const getDepartment = (departmentId, organisationId) => {
        const departments = getOrganisationDepartments(organisationId);
        const department = departments.find(d => d.id === departmentId);
        return department;
    };


    return (
        <>
            <Modal
                title="Копирование заявки"
                open={visible}
                onCancel={handleModalCancel}
                width={1000}
                footer={[
                    <Tooltip key="back" title="Закрыть модальное окно без сохранения изменений">
                        <Button onClick={handleModalCancel}>
                            Закрыть
                        </Button>
                    </Tooltip>,
                    <Tooltip key="draft" title="Сохранить заявку как черновик для последующего редактирования">
                        <Button
                            onClick={() => handleFormSubmit(true)}
                            loading={loading}
                        >
                            Сохранить как черновик
                        </Button>
                    </Tooltip>,
                    <Tooltip key="submit" title="Создать заявку и отправить на согласование">
                        <Button
                            type="primary"
                            loading={loading}
                            onClick={() => handleFormSubmit(false)}
                        >
                            Создать заявку
                        </Button>
                    </Tooltip>
                ]}
                destroyOnClose
            >
                {originalRequest && (
                    <div>
                        <div style={{
                            background: '#f5f5f5',
                            padding: '16px',
                            borderRadius: '6px',
                            marginBottom: '24px'
                        }}>
                            <h4 style={{ margin: '0 0 12px 0', color: '#666' }}>
                                Копируем заявку: {originalRequest.job_title}
                            </h4>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                <div>Компания: {getOrganisationName(originalRequest.organisation)}</div>
                                <div>Отдел: {getDepartment(originalRequest.department, originalRequest.organisation)?.name || 'Не указано'}</div>
                                <div>Количество позиций: {originalRequest.positions_count}</div>
                            </div>
                        </div>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            onFieldsChange={handleFormChange}
                            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                        >
                            {/* Согласование */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Согласование
                                </h3>

                                <Form.Item name="company" label="Компания" rules={getValidationRules('company', false)}>
                                    <Select
                                        showSearch
                                        optionFilterProp="children"
                                        placeholder="Выберите компанию из доступных для вас"
                                        onChange={(value) => {
                                            setSelectedOrganisation(value);
                                            filterDepartments(value);
                                            form.setFieldsValue({ department: undefined });
                                        }}
                                    >
                                        {organisations?.map(org => (
                                            <Option key={org.organisation_id} value={org.organisation_id}>{org.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.List name="approver_management">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map(({ key, name, ...restField }) => (
                                                <Row gutter={8} key={key}>
                                                    <Col span={22}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name]}
                                                            label={`Согласующий №${name + 1}`}
                                                            rules={[{ required: true, message: 'Выберите согласующего' }]}
                                                        >
                                                            <Select
                                                                placeholder="Выберите согласующего"
                                                                showSearch
                                                                optionFilterProp="children"
                                                                filterOption={(input, option) =>
                                                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                                }
                                                            >
                                                                {users?.map(u => (
                                                                    <Option key={u.id} value={u.id}>
                                                                        {u.last_name} {u.first_name} {u.middle_name || ''}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={2} style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                                                        <Button danger onClick={() => remove(name)}>
                                                            Удалить
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Form.Item>
                                                <Button
                                                    type="dashed"
                                                    onClick={() => add()}
                                                    disabled={!users || users.length === 0}
                                                >
                                                    Добавить согласующего
                                                </Button>
                                            </Form.Item>
                                        </>
                                    )}
                                </Form.List>

                                {/* <Form.Item
                                    name="approver_hr"
                                    label="Согласующий HR"
                                    rules={[{ required: true, message: 'Выберите согласующего HR' }]}
                                >
                                    <Select
                                        placeholder="Выберите согласующего HR"
                                        showSearch
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {users?.map(u => (
                                            <Option key={u.id} value={u.id}>
                                                {u.last_name} {u.first_name} {u.middle_name || ''}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item> */}
                            </div>

                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Основная информация
                                </h3>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="job_title"
                                            label="Название должности"
                                            rules={getValidationRules('job_title', false)}
                                        >
                                            <Input placeholder="Введите название должности" />
                                        </Form.Item>

                                        <Form.Item
                                            name="department"
                                            label="Отдел"
                                            rules={getValidationRules('department', false)}
                                        >
                                            <Select placeholder="Выберите отдел" disabled={!selectedOrganisation}>
                                                {filteredDepartments.map(d => (
                                                    <Option key={d.id} value={d.id}>{d.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>

                                        <Form.Item name="city" label="Город" rules={getValidationRules('city', false)}>
                                            <Input />
                                        </Form.Item>

                                        <Form.Item name="submission_date" label="Дата подачи заявки" rules={getValidationRules('submission_date', false)}>
                                            <DatePicker style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>

                                    <Col span={12}>
                                        <Form.Item name="author_name" label="Автор заявки (ФИО)" rules={getValidationRules('author_name', false)}>
                                            <Input readOnly />
                                        </Form.Item>

                                        <Form.Item name="author_position" label="Автор заявки (должность)" rules={getValidationRules('author_position', false)}>
                                            <Input />
                                        </Form.Item>

                                        <Form.Item
                                            name="planned_start_date"
                                            label="Планируемая дата выхода сотрудника на работу"
                                            rules={getValidationRules('planned_start_date', false)}
                                        >
                                            <Input style={{ width: '100%' }} placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                                        </Form.Item>

                                        <Form.Item
                                            name="positions_count"
                                            label="Количество позиций"
                                            rules={getValidationRules('positions_count', false)}
                                        >
                                            <InputNumber min={1} style={{ width: '100%' }} placeholder="Количество позиций" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Информация о вакансии */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Информация о вакансии
                                </h3>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="rate"
                                            label="Ставка"
                                            rules={getValidationRules('rate', false)}
                                        >
                                            <Select placeholder="Выберите ставку">
                                                {['0.25', '0.5', '0.75', '1', '1.25', '1.5', '1.75', '2'].map(value => (
                                                    <Option key={value} value={value}>
                                                        {value}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>

                                        <Form.Item name="supervisor" label="Начальник" rules={getValidationRules('supervisor', false)}>
                                            <Input placeholder="Введите ФИО начальника" />
                                        </Form.Item>

                                        <Form.Item name="is_material_responsible" hidden>
                                            <Input />
                                        </Form.Item>

                                        <Form.Item label="Материально ответственное лицо">
                                            <Button
                                                type={isMaterialResponsible ? "primary" : "default"}
                                                onClick={() => handleCheckboxToggle('is_material_responsible')}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: isMaterialResponsible ? 'bold' : 'normal'
                                                }}
                                            >
                                                {isMaterialResponsible ? '✓ Материально ответственное лицо' : 'Материально ответственное лицо'}
                                            </Button>
                                        </Form.Item>
                                    </Col>

                                    <Col span={12}>
                                        <Form.Item name="subordinates_count" label="Подчиненные (количество)" rules={getValidationRules('subordinates_count', false)}>
                                            <InputNumber min={0} style={{ width: '100%' }} placeholder="Количество подчиненных" />
                                        </Form.Item>


                                        <Form.Item name="vacancy_reason" label="Причина возникновения вакансии" rules={getValidationRules('vacancy_reason', false)}>
                                            <Select placeholder="Выберите причину">
                                                {vacancyReasons.map(reason => (
                                                    <Option key={reason} value={reason}>{reason}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Должностные обязанности */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Должностные обязанности
                                </h3>

                                <Form.Item name="tasks" label="Задачи" rules={getValidationRules('tasks', false)}>
                                    <TextArea rows={3} placeholder="Опишите задачи" />
                                </Form.Item>

                                <Form.Item name="functional_responsibilities" label="Функциональные обязанности" rules={getValidationRules('functional_responsibilities', false)}>
                                    <TextArea rows={3} placeholder="Опишите функциональные обязанности" />
                                </Form.Item>

                                <Form.Item name="reporting" label="Отчетность о проделанной работе">
                                    <TextArea rows={3} placeholder="Опишите отчетность" />
                                </Form.Item>

                                <Form.Item name="expected_results" label="Ожидаемые результаты" rules={getValidationRules('expected_results', false)}>
                                    <TextArea rows={3} placeholder="Опишите ожидаемые результаты" />
                                </Form.Item>
                            </div>

                            {/* Условия работы */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Условия работы
                                </h3>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="work_schedule" label="График работы" rules={getValidationRules('work_schedule', false)}>
                                            <Select placeholder="Выберите график работы">
                                                {workSchedules.map(schedule => (
                                                    <Option key={schedule} value={schedule}>{schedule}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>

                                        <Form.Item name="work_start_time" label="Начало работы" rules={getValidationRules('work_start_time', false)}>
                                            <TimePicker
                                                style={{ width: '100%' }}
                                                format="HH:mm"
                                                needConfirm={false}
                                                placeholder="Выберите время начала работы"
                                            />
                                        </Form.Item>

                                        <Form.Item label="Командировки">
                                            <Button
                                                type={requiresTravel ? "primary" : "default"}
                                                onClick={() => setRequiresTravel(!requiresTravel)}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: requiresTravel ? 'bold' : 'normal'
                                                }}
                                            >
                                                {requiresTravel ? '✓ Командировки' : 'Командировки'}
                                            </Button>
                                        </Form.Item>

                                        {requiresTravel && (
                                            <Form.Item name="business_trips_details" label="Частота и длительность командировок">
                                                <Input placeholder="Например: 2 раза в месяц, 3-5 дней" />
                                            </Form.Item>
                                        )}

                                    </Col>

                                    <Col span={12}>
                                        <Form.Item name="work_location_ratio" label="Соотношение работы: дом/офис">
                                            <Input placeholder="Например: 70% офис, 30% дом" />
                                        </Form.Item>

                                        <Form.Item name="work_end_time" label="Окончание работы" rules={getValidationRules('work_end_time', false)}>
                                            <TimePicker
                                                style={{ width: '100%' }}
                                                format="HH:mm"
                                                needConfirm={false}
                                                placeholder="Выберите время окончания работы"
                                            />
                                        </Form.Item>

                                        <Form.Item label="Готовность к переезду">
                                            <Button
                                                type={requiresRelocation ? "primary" : "default"}
                                                onClick={() => setRequiresRelocation(!requiresRelocation)}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: requiresRelocation ? 'bold' : 'normal'
                                                }}
                                            >
                                                {requiresRelocation ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Зарплата */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Зарплата
                                </h3>

                                <Form.Item name="salary_structure" label="Заработная плата (структура)" rules={getValidationRules('salary_structure', false)}>
                                    <TextArea rows={2} placeholder="Опишите как формируется заработная плата" />
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Зарплата на период испытательного срока (BYN)"
                                            required
                                            validateStatus={form.getFieldError('probation_salary_min')?.length > 0 || form.getFieldError('probation_salary_max')?.length > 0 ? 'error' : ''}
                                            help={form.getFieldError('probation_salary_min')?.[0] || form.getFieldError('probation_salary_max')?.[0]}
                                        >
                                            <Input.Group compact>
                                                <Form.Item name="probation_salary_min" noStyle rules={getValidationRules('probation_salary_min', false)}>
                                                    <InputNumber
                                                        min={0}
                                                        placeholder="От"
                                                        style={{ width: '50%' }}
                                                        parser={value => value.replace(/\s?BYN/g, '')}
                                                    />
                                                </Form.Item>
                                                <Form.Item name="probation_salary_max" noStyle rules={getValidationRules('probation_salary_max', false)}>
                                                    <InputNumber
                                                        min={0}
                                                        placeholder="До"
                                                        style={{ width: '50%' }}
                                                        parser={value => value.replace(/\s?BYN/g, '')}
                                                    />
                                                </Form.Item>
                                            </Input.Group>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Зарплата после испытательного срока (BYN)"
                                            required
                                            validateStatus={form.getFieldError('regular_salary_min')?.length > 0 || form.getFieldError('regular_salary_max')?.length > 0 ? 'error' : ''}
                                            help={form.getFieldError('regular_salary_min')?.[0] || form.getFieldError('regular_salary_max')?.[0]}
                                        >
                                            <Input.Group compact>
                                                <Form.Item name="regular_salary_min" noStyle rules={getValidationRules('regular_salary_min', false)}>
                                                    <InputNumber
                                                        min={0}
                                                        placeholder="От"
                                                        style={{ width: '50%' }}
                                                        parser={value => value.replace(/\s?BYN/g, '')}
                                                    />
                                                </Form.Item>
                                                <Form.Item name="regular_salary_max" noStyle rules={getValidationRules('regular_salary_max', false)}>
                                                    <InputNumber
                                                        min={0}
                                                        placeholder="До"
                                                        style={{ width: '50%' }}
                                                        parser={value => value.replace(/\s?BYN/g, '')}
                                                    />
                                                </Form.Item>
                                            </Input.Group>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        {/* Компенсация связи */}
                                        <Form.Item name="communication_compensation" valuePropName="checked" style={{ display: 'none' }}>
                                            <Input type="hidden" />
                                        </Form.Item>
                                        <Form.Item label="Компенсация затрат: связь">
                                            <Button
                                                type={compensatePhone ? "primary" : "default"}
                                                onClick={() => {
                                                    const newValue = !compensatePhone;
                                                    setCompensatePhone(newValue);
                                                    form.setFieldsValue({ communication_compensation: newValue });
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: compensatePhone ? 'bold' : 'normal'
                                                }}
                                            >
                                                {compensatePhone ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        {/* Компенсация автомобиля */}
                                        <Form.Item name="car_compensation" valuePropName="checked" style={{ display: 'none' }}>
                                            <Input type="hidden" />
                                        </Form.Item>
                                        <Form.Item label="Компенсация затрат: личный автомобиль">
                                            <Button
                                                type={compensateCar ? "primary" : "default"}
                                                onClick={() => {
                                                    const newValue = !compensateCar;
                                                    setCompensateCar(newValue);
                                                    form.setFieldsValue({ car_compensation: newValue });
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: compensateCar ? 'bold' : 'normal'
                                                }}
                                            >
                                                {compensateCar ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item name="work_address" label="Адрес рабочего места" rules={getValidationRules('work_address', false)}>
                                    <Input placeholder="Полный адрес рабочего места" />
                                </Form.Item>
                            </div>

                            {/* Требования к кандидату */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Требования к кандидату
                                </h3>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="gender" label="Пол">
                                            <Select options={[
                                                { value: 'Мужской', label: 'Мужской' },
                                                { value: 'Женский', label: 'Женский' },
                                                { value: 'Не имеет значения', label: 'Не имеет значения' },
                                            ]} />
                                        </Form.Item>
                                        <Form.Item name="marital_status" label="Семейное положение">
                                            <Select
                                                mode="multiple"
                                                options={[
                                                    { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                                                    { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                                                    { value: 'Разведен(а)', label: 'Разведен(а)' },
                                                    { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                                                    { value: 'Не имеет значения', label: 'Не имеет значения' },
                                                ]}
                                            />
                                        </Form.Item>

                                        <Form.Item label="Наличие водительского удостоверения">
                                            <Button
                                                type={requiresLicense ? "primary" : "default"}
                                                onClick={() => setRequiresLicense(!requiresLicense)}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: requiresLicense ? 'bold' : 'normal'
                                                }}
                                            >
                                                {requiresLicense ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                                            </Button>
                                        </Form.Item>

                                        {requiresLicense && (
                                            <Form.Item name="driver_license_categories" label="Категории водительских прав">
                                                <Select
                                                    mode="multiple"
                                                    options={[
                                                        'АМ', 'А', 'А1', 'В', 'С', 'D', 'ВЕ', 'СЕ', 'DE', 'F', 'I'
                                                    ].map(cat => ({ value: cat, label: cat }))}
                                                />
                                            </Form.Item>
                                        )}
                                    </Col>

                                    <Col span={12}>
                                        <Form.Item name="age" label="Возраст">
                                            <Input placeholder="Укажите возраст или диапазон" />
                                        </Form.Item>

                                        <Form.Item name="education" label="Образование">
                                            <Select
                                                mode="multiple"
                                                options={[
                                                    'Базовое',
                                                    'Среднее',
                                                    'Профессионально-техническое',
                                                    'Среднее специальное',
                                                    'Высшее'
                                                ].map(ed => ({ value: ed, label: ed }))}
                                            />
                                        </Form.Item>

                                        <Form.Item label="Наличие личного автомобиля">
                                            <Button
                                                type={requiresCar ? "primary" : "default"}
                                                onClick={() => setRequiresCar(!requiresCar)}
                                                style={{
                                                    width: '100%',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: requiresCar ? 'bold' : 'normal'
                                                }}
                                            >
                                                {requiresCar ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Навыки и опыт */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Навыки и опыт
                                </h3>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="computer_skills" label="Уровень владения ПК (Word, Excel и др.)">
                                            <Select
                                                options={[
                                                    'Не требуется',
                                                    'Базовый',
                                                    'Уверенный',
                                                    'Продвинутый'
                                                ].map(level => ({ value: level, label: level }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name="skills_1c" label="Навыки в работе с 1С">
                                            <Input.TextArea rows={1} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item name="work_experience" label="Опыт работы (область, продолжительность)">
                                    <TextArea rows={2} />
                                </Form.Item>
                            </div>

                            {/* Личные и профессиональные качества */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Личные и профессиональные качества
                                </h3>

                                <Form.Item name="personal_qualities" label="Личные качества кандидатов">
                                    <TextArea rows={3} />
                                </Form.Item>

                                <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов">
                                    <TextArea rows={3} />
                                </Form.Item>
                            </div>

                            {/* Дополнительные требования */}
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                                    Дополнительные требования
                                </h3>

                                <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов">
                                    <TextArea rows={2} />
                                </Form.Item>

                                <Form.Item name="undesired_companies" label="Компании, из которых нежелательно рассматривать кандидатов">
                                    <TextArea rows={2} />
                                </Form.Item>

                                <Form.Item name="other_conditions" label="Другие условия">
                                    <TextArea rows={3} />
                                </Form.Item>
                            </div>
                        </Form>
                    </div>
                )}
            </Modal>

            <ConfirmModal
                visible={showConfirm}
                onConfirm={handleConfirmClose}
                onCancel={() => setShowConfirm(false)}
                title="Подтверждение закрытия"
                message="Данные не будут сохранены. Продолжить?"
                okText="Продолжить"
                cancelText="Отмена"
                type="warning"
            />
        </>
    );
};

export default CopyModal;
